import React, { useEffect, useState } from "react";
import axios from "axios";
import "./ContenedorAnuncios.css";
import { obtenerPostsPorClase, crearPost, crearRepost, obtenerReposts } from "../Pages/constants";
import { useData } from "../Pages/DataContext";

const formatDate = (value) => {
  if (!value) return "Fecha no disponible";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });
};

const initials = (name, lastName) => `${name?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase() || "U";

export const ContenedorAnuncios = () => {
  const { dataClase, data } = useData();
  const idClase = dataClase?.id_clase || null;
  const [posts, setPosts] = useState([]);
  const [reposts, setReposts] = useState({});
  const [nuevoPost, setNuevoPost] = useState("");
  const [repostContent, setRepostContent] = useState({});
  const [status, setStatus] = useState({ type: "loading", text: "Cargando publicaciones..." });
  const [publishing, setPublishing] = useState(false);
  const [replyingId, setReplyingId] = useState(null);

  useEffect(() => {
    if (idClase) fetchPosts(idClase);
    else setStatus({ type: "error", text: "Selecciona una clase para consultar su foro." });
  }, [idClase]);

  const fetchPosts = async (classId) => {
    setStatus({ type: "loading", text: "Cargando publicaciones..." });
    try {
      const response = await axios.get(obtenerPostsPorClase(classId));
      const nextPosts = Array.isArray(response.data) ? response.data : [];
      setPosts(nextPosts);
      const entries = await Promise.all(nextPosts.map(async (post) => {
        try {
          const result = await axios.get(obtenerReposts(post.id_post));
          return [post.id_post, Array.isArray(result.data) ? result.data : []];
        } catch {
          return [post.id_post, []];
        }
      }));
      setReposts(Object.fromEntries(entries));
      setStatus({ type: "", text: "" });
    } catch (error) {
      console.error("Error al obtener publicaciones:", error);
      setStatus({ type: "error", text: "No se pudieron cargar las publicaciones. Intenta nuevamente." });
    }
  };

  const handleCrearPost = async () => {
    if (!nuevoPost.trim()) {
      setStatus({ type: "error", text: "Escribe un mensaje antes de publicar." });
      return;
    }
    try {
      setPublishing(true);
      setStatus({ type: "", text: "" });
      const response = await axios.post(crearPost, { id_clase: idClase, user_id: data.id, content: nuevoPost.trim() });
      if (!response.data.success) throw new Error(response.data.message || "No se pudo publicar.");
      setNuevoPost("");
      setStatus({ type: "success", text: "Tu publicación se agregó al foro." });
      await fetchPosts(idClase);
    } catch (error) {
      console.error("Error al crear publicación:", error);
      setStatus({ type: "error", text: error.message || "No se pudo crear la publicación." });
    } finally {
      setPublishing(false);
    }
  };

  const handleRepost = async (idPost) => {
    const content = repostContent[idPost]?.trim();
    if (!content) {
      setStatus({ type: "error", text: "Escribe una respuesta antes de enviarla." });
      return;
    }
    try {
      setReplyingId(idPost);
      const response = await axios.post(crearRepost(idPost), { user_id: data.id, content });
      if (!response.data.success) throw new Error(response.data.message || "No se pudo responder.");
      setRepostContent((previous) => ({ ...previous, [idPost]: "" }));
      setStatus({ type: "success", text: "Tu respuesta se agregó a la conversación." });
      await fetchPosts(idClase);
    } catch (error) {
      console.error("Error al responder:", error);
      setStatus({ type: "error", text: error.message || "No se pudo agregar la respuesta." });
    } finally {
      setReplyingId(null);
    }
  };

  return (
    <div className="forum-feed">
      <div className="forum-section-heading">
        <div>
          <span>Conversación de la clase</span>
          <h2>Publicaciones recientes</h2>
        </div>
        <strong>{posts.length} {posts.length === 1 ? "publicación" : "publicaciones"}</strong>
      </div>

      <section className="forum-composer">
        <label htmlFor="new-forum-post">Comparte algo con tu grupo</label>
        <textarea id="new-forum-post" placeholder="Escribe una pregunta, idea o comentario..." value={nuevoPost} onChange={(event) => setNuevoPost(event.target.value)} maxLength={2000} />
        <div className="forum-composer-footer">
          <span>{nuevoPost.length}/2000</span>
          <button type="button" onClick={handleCrearPost} disabled={publishing || !idClase}>{publishing ? "Publicando..." : "Publicar"}</button>
        </div>
      </section>

      {status.text && <div className={`forum-status forum-status-${status.type}`} role="status">{status.text}</div>}

      {!status.text && posts.length === 0 && (
        <div className="forum-empty">
          <div aria-hidden="true">01</div>
          <h3>Inicia la conversación</h3>
          <p>Todavía no hay publicaciones en este curso.</p>
        </div>
      )}

      <div className="forum-posts">
        {posts.map((post) => (
          <article className="forum-post" key={post.id_post}>
            <header>
              <div className="forum-avatar">{initials(post.nombre, post.apellido)}</div>
              <div>
                <h3>{post.nombre} {post.apellido}</h3>
                <time>{formatDate(post.created_at)}</time>
              </div>
            </header>
            <p className="forum-post-content">{post.content}</p>

            {!!reposts[post.id_post]?.length && (
              <div className="forum-replies">
                <span className="forum-replies-title">{reposts[post.id_post].length} {reposts[post.id_post].length === 1 ? "respuesta" : "respuestas"}</span>
                {reposts[post.id_post].map((reply, index) => (
                  <div className="forum-reply" key={reply.id_repost || index}>
                    <div className="forum-avatar forum-avatar-small">{initials(reply.nombre, reply.apellido)}</div>
                    <div><strong>{reply.nombre} {reply.apellido}</strong><p>{reply.content}</p></div>
                  </div>
                ))}
              </div>
            )}

            <div className="forum-reply-box">
              <textarea aria-label={`Responder a ${post.nombre || "la publicación"}`} placeholder="Escribe una respuesta..." value={repostContent[post.id_post] || ""} onChange={(event) => setRepostContent((previous) => ({ ...previous, [post.id_post]: event.target.value }))} />
              <button type="button" onClick={() => handleRepost(post.id_post)} disabled={replyingId === post.id_post}>{replyingId === post.id_post ? "Enviando..." : "Responder"}</button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};
